import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, onAuthStateChanged, signInWithPopup, signOut, type Auth } from 'firebase/auth';
import type { Role } from '../types/v2';

export type AuthStatus = 'loading' | 'unauthenticated' | 'forbidden' | 'authorized' | 'config-error';

export interface AuthContextValue {
  status: AuthStatus;
  /**
   * The analytics role from the token's custom claims. The interface uses it to hide what a role cannot open; the API
   * enforces the same rules on every request, so hiding is never the control.
   */
  role: Role | null;
  displayName: string | null;
  isDevBypass: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

let firebaseApp: FirebaseApp | undefined;
let firebaseAuth: Auth | undefined;

function getFirebaseAuth(): Auth | null {
  const { VITE_FIREBASE_API_KEY: apiKey, VITE_FIREBASE_AUTH_DOMAIN: authDomain, VITE_FIREBASE_PROJECT_ID: projectId, VITE_FIREBASE_APP_ID: appId } = import.meta.env;
  if (!apiKey || !authDomain || !projectId || !appId) return null;
  firebaseApp ??= initializeApp({ apiKey, authDomain, projectId, appId });
  firebaseAuth ??= getAuth(firebaseApp);
  return firebaseAuth;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const devBypass = import.meta.env.DEV && import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';
  const [status, setStatus] = useState<AuthStatus>(devBypass ? 'authorized' : 'loading');
  const [role, setRole] = useState<Role | null>(devBypass ? 'admin' : null);
  const [displayName, setDisplayName] = useState<string | null>(devBypass ? 'Local developer' : null);

  useEffect(() => {
    if (devBypass) return;
    const auth = getFirebaseAuth();
    if (!auth) {
      setStatus('config-error');
      return;
    }
    return onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setDisplayName(null);
        setRole(null);
        setStatus('unauthenticated');
        return;
      }
      setDisplayName(user.displayName || user.email || 'Signed in');
      const token = await user.getIdTokenResult(true);
      const next = roleFromClaims(token.claims);
      setRole(next);
      setStatus(next ? 'authorized' : 'forbidden');
    });
  }, [devBypass]);

  const value = useMemo<AuthContextValue>(() => ({
    status,
    role,
    displayName,
    isDevBypass: devBypass,
    login: async () => {
      const auth = getFirebaseAuth();
      if (!auth) throw new Error('Firebase Auth is not configured.');
      await signInWithPopup(auth, new GoogleAuthProvider());
    },
    logout: async () => {
      const auth = getFirebaseAuth();
      if (auth) await signOut(auth);
    },
    getAccessToken: async () => getFirebaseAuth()?.currentUser?.getIdToken() ?? null,
  }), [devBypass, displayName, role, status]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Same rule as the API (api/src/auth.ts roleOf): the admin claim wins, then analyticsRole. */
export function roleFromClaims(claims: Record<string, unknown>): Role | null {
  if (claims.admin === true) return 'admin';
  if (claims.analyticsRole === 'analyst') return 'analyst';
  if (claims.analyticsRole === 'commercial') return 'commercial';
  return null;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
